/**
 * databaseInspector.js — Rê Leve
 *
 * Inspeciona TODAS as "tabelas" da Rê Leve. Hoje persistem em localStorage
 * do browser; quando o backend Postgres (Neon) estiver ativo, esse arquivo
 * vai apontar para /api/admin/tables ao invés de localStorage.
 *
 * Tabelas catalogadas com metadados: descrição, schema esperado, tipo,
 * número de registros, tamanho em bytes.
 */

import { publish, EVT } from './liveSyncEngine';

// Catálogo de "tabelas" (chaves do localStorage)
export const TABLE_CATALOG = [
  {
    key: 'releve.knowledge.rag',
    name: 'RAG · Base de conhecimento',
    icon: '📚',
    description: 'Trechos textuais que a assistente consulta antes de responder.',
    schema: 'id, topic, text, tags[], source, priority, createdAt',
    futureBackend: 'postgres: rag_entries',
  },
  {
    key: 'releve.knowledge.guardrails',
    name: 'Guardrails',
    icon: '🛡️',
    description: 'Regras de proteção que vencem o LLM (G01..Gxx).',
    schema: 'id, name, rule, severity, active',
    futureBackend: 'postgres: guardrails',
  },
  {
    key: 'releve.knowledge.context',
    name: 'Contexto da empresa',
    icon: '🏢',
    description: 'Variáveis de negócio (horário, bairros, prazos, fornecedores).',
    schema: 'id, key, value, editable',
    futureBackend: 'postgres: business_context',
  },
  {
    key: 'releve.knowledge.learned',
    name: 'Aprendizados',
    icon: '🌱',
    description: 'O que a equipe ensinou após aprovar perguntas pendentes.',
    schema: 'id, fromQuestion, answer, tags[], customerSegment, answeredAt',
    futureBackend: 'postgres: learnings',
  },
  {
    key: 'releve.knowledge.log',
    name: 'Log de auditoria',
    icon: '📜',
    description: 'Cada operação CRUD na base (add/update/delete) com timestamp.',
    schema: 'ts, type, payload',
    futureBackend: 'postgres: audit_log',
  },
  {
    key: 'releve.pendingQuestions',
    name: 'Aprovações pendentes',
    icon: '📥',
    description: 'Perguntas que a assistente encaminhou para a equipe.',
    schema: 'id, type, customerId, customerName, question, suggestedAnswer, status, askedAt',
    futureBackend: 'postgres: approvals',
  },
  {
    key: 'releve.chatEvents.global',
    name: 'Eventos do chat',
    icon: '💬',
    description: 'Cada mensagem (user/bot) registrada com timestamp, intent, source.',
    schema: 'ts, role, text, clienteId, intent, source, useCase',
    futureBackend: 'postgres: chat_events',
  },
  {
    key: 'releve.eventBuffer',
    name: 'Buffer de eventos live-sync',
    icon: '⚡',
    description: 'Últimos 200 eventos do liveSyncEngine (chat + pedidos + aprovações).',
    schema: 'id, type, payload, ts',
    futureBackend: 'postgres: event_stream (Kafka)',
  },
  {
    key: 'releve.wishlist',
    name: 'Lista de desejos dos clientes',
    icon: '⭐',
    description: 'Ingredientes e pratos que clientes pediram para ver no cardápio.',
    schema: 'id, customerId, customerName, type, value, priority, notes, at',
    futureBackend: 'postgres: customer_wishes',
  },
  {
    key: 'releve.assistantName',
    name: 'Nome da assistente',
    icon: '🌱',
    description: 'Nome configurado para a assistente (default: "Léve").',
    schema: 'string puro (não é JSON)',
    futureBackend: 'postgres: settings.assistant_name',
  },
  {
    key: 'releve.credentials.meta',
    name: 'Status das credenciais',
    icon: '🔑',
    description: 'Metadados das integrações (status: pending/configured/active). Chaves reais NÃO ficam aqui — vão pro Render.',
    schema: '{ openai, n8n, stripe, neon, whatsapp }, cada um com status/configuredAt',
    futureBackend: 'postgres: integrations',
  },
  {
    key: 'releve.nutrition.custom',
    name: 'Alimentos personalizados',
    icon: '🥗',
    description: 'Alimentos adicionados pela equipe além da base padrão TACO/USDA.',
    schema: 'name, aliases[], kcal, protein, carb, fat, fiber, group',
    futureBackend: 'postgres: custom_foods',
  },
];

// ── Leitura ───────────────────────────────────────────────────────────────
export function getTable(key) {
  if (typeof window === 'undefined') return { key, exists: false, raw: null, parsed: null, size: 0, count: 0 };
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return { key, exists: false, raw: null, parsed: null, size: 0, count: 0 };
    let parsed = null;
    try { parsed = JSON.parse(raw); } catch { parsed = raw; }
    const size = new Blob([raw]).size;
    const count = Array.isArray(parsed) ? parsed.length
      : (parsed && typeof parsed === 'object' ? Object.keys(parsed).length : (raw ? 1 : 0));
    return { key, exists: true, raw, parsed, size, count };
  } catch (e) {
    return { key, exists: false, raw: null, parsed: null, size: 0, count: 0, error: e.message };
  }
}

export function getAllTables() {
  return TABLE_CATALOG.map(meta => ({ ...meta, ...getTable(meta.key) }));
}

export function getStorageUsage() {
  if (typeof window === 'undefined') return { used: 0, quota: 0, pct: 0 };
  let used = 0;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      const v = localStorage.getItem(k);
      used += new Blob([k + v]).size;
    }
  } catch {}
  // Browsers tipicamente dão 5–10 MB por origin
  const quota = 5 * 1024 * 1024;
  return { used, quota, pct: (used / quota) * 100 };
}

// ── Escrita ───────────────────────────────────────────────────────────────
export function clearTable(key) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(key);
    publish(EVT.SYNC_STATUS, { action: 'table-cleared', key });
  } catch {}
}

export function clearAllTables() {
  if (typeof window === 'undefined') return;
  TABLE_CATALOG.forEach(t => {
    try { localStorage.removeItem(t.key); } catch {}
  });
  publish(EVT.SYNC_STATUS, { action: 'all-tables-cleared' });
}

export function exportTable(key) {
  const t = getTable(key);
  if (!t.exists) return null;
  const blob = new Blob([JSON.stringify(t.parsed, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${key.replace(/\./g, '-')}-${new Date().toISOString().slice(0, 10)}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

export function exportAllTables() {
  const dump = {};
  TABLE_CATALOG.forEach(t => {
    const data = getTable(t.key);
    if (data.exists) dump[t.key] = data.parsed;
  });
  const blob = new Blob([JSON.stringify({
    exportedAt: new Date().toISOString(),
    domain: typeof window !== 'undefined' ? window.location.host : '',
    tables: dump,
  }, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `releve-database-backup-${new Date().toISOString().slice(0, 10)}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

export function importAllTables(jsonString) {
  try {
    const data = JSON.parse(jsonString);
    if (!data.tables) throw new Error('Formato inválido: faltou "tables"');
    Object.entries(data.tables).forEach(([key, value]) => {
      try {
        const serialized = typeof value === 'string' ? value : JSON.stringify(value);
        localStorage.setItem(key, serialized);
      } catch {}
    });
    publish(EVT.SYNC_STATUS, { action: 'imported', tables: Object.keys(data.tables) });
    return { ok: true, count: Object.keys(data.tables).length };
  } catch (e) {
    return { ok: false, error: e.message };
  }
}

export function formatBytes(n) {
  if (n < 1024) return n + ' B';
  if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB';
  return (n / 1024 / 1024).toFixed(2) + ' MB';
}
